'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { useOpenStatusLabel } from './OpenStatusBadge'
import type { OpenStatus } from '@/lib/hours/openStatus'

type Props = {
  businessName: string
  tagline?: string
  heroImageUrl?: string
  heroVideoUrl?: string
  city?: string | null
  pickupWaitMinutes?: number
  readyInLabel?: string
  onViewMenu: () => void
  onDelivery?: () => void
  hasDelivery?: boolean
  openStatus?: OpenStatus | null
}

/** Coral de firma del tema costa (chip de delivery, acentos del hero). */
const CORAL = '#ff6f61'

/** Onda hairline — kicker del tema costa. */
function Wave({ stroke }: { stroke: string }) {
  return (
    <svg width="44" height="10" viewBox="0 0 44 10" fill="none" aria-hidden>
      <path
        d="M1 5c3.7-4 7.3-4 11 0s7.3 4 11 0 7.3-4 11 0 5.5 3 9 1"
        stroke={stroke}
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  )
}

/**
 * Hero del tema "costa" (fresco costero): el PRIMER hero claro del catálogo.
 * Composición partida sobre fondo sal-de-mar — texto a la izquierda con aire,
 * foto a la derecha como "postal" redondeada bajo un velo sol-lavado (token
 * heroOverlay, que aquí es claro en la base). Onda hairline como kicker, chip
 * coral para delivery y divisor de ola cerrando la sección. La barra superior
 * usa modo sólido (navSolid). Lee tokens del tema (--ms-*).
 */
export function HeroCosta({
  businessName,
  tagline,
  heroImageUrl,
  heroVideoUrl,
  city,
  pickupWaitMinutes,
  readyInLabel,
  onViewMenu,
  onDelivery,
  hasDelivery,
  openStatus,
}: Props) {
  const t = useTranslations('public.microsite')
  const statusLabel = useOpenStatusLabel(openStatus ?? null)
  const hasMedia = !!(heroImageUrl || heroVideoUrl)

  return (
    <section className="relative w-full overflow-hidden" style={{ background: 'var(--ms-bg)' }}>
      {/* pt despeja la barra superior fija (navSolid) */}
      <div className="relative mx-auto max-w-6xl px-5 sm:px-8 pt-24 sm:pt-28 pb-14 sm:pb-16">
        <div className={`grid items-center gap-10 sm:gap-12 ${hasMedia ? 'md:grid-cols-[1.05fr_0.95fr]' : ''}`}>
          {/* Columna texto */}
          <div className="min-w-0">
            <div className="flex items-center gap-3 mb-5">
              <Wave stroke="var(--ms-accent)" />
              {city && (
                <span
                  className="text-[11px] font-bold uppercase tracking-[0.24em]"
                  style={{ color: 'var(--ms-text-muted)' }}
                >
                  {city}
                </span>
              )}
            </div>

            <h1
              className="leading-[1.02] tracking-[0.01em] text-[clamp(40px,7vw,72px)]"
              style={{ fontFamily: 'var(--ms-font-display)', color: 'var(--ms-text)' }}
            >
              {businessName}
            </h1>

            {tagline && (
              <p className="mt-4 max-w-lg text-[16px] sm:text-[17.5px] leading-relaxed" style={{ color: 'var(--ms-text-soft)' }}>
                {tagline}
              </p>
            )}

            {/* Meta discreta */}
            <div
              className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[13px]"
              style={{ color: 'var(--ms-text-muted)' }}
            >
              {statusLabel && (
                <span className="inline-flex items-center gap-1.5">
                  <span
                    className="w-[6px] h-[6px] rounded-full"
                    style={{
                      background:
                        statusLabel.tone === 'open'
                          ? '#18b89b'
                          : statusLabel.tone === 'soon'
                            ? '#f59e0b'
                            : 'var(--ms-text-muted)',
                    }}
                  />
                  {statusLabel.text}
                </span>
              )}
              {statusLabel && <span aria-hidden>·</span>}
              <span>{t('pickupAvailable')}</span>
              {pickupWaitMinutes ? (
                <>
                  <span aria-hidden>·</span>
                  <span>{readyInLabel ?? t('readyIn', { minutes: pickupWaitMinutes })}</span>
                </>
              ) : null}
            </div>

            {/* CTAs */}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={onViewMenu}
                className="inline-flex items-center rounded-full px-8 py-3.5 text-[15px] font-bold transition hover:brightness-105 active:scale-[0.98]"
                style={{
                  background: 'var(--ms-accent)',
                  color: '#ffffff',
                  boxShadow: '0 14px 30px -14px color-mix(in srgb, var(--ms-accent) 70%, transparent)',
                }}
              >
                {t('orderOnline')}
              </button>
              {hasDelivery && onDelivery && (
                <button
                  type="button"
                  onClick={onDelivery}
                  className="inline-flex items-center rounded-full px-6 py-3.5 text-[14.5px] font-semibold transition hover:brightness-95"
                  style={{
                    color: CORAL,
                    border: `1.5px solid color-mix(in srgb, ${CORAL} 55%, transparent)`,
                    background: 'var(--ms-surface)',
                  }}
                >
                  {t('delivery')}
                </button>
              )}
            </div>
          </div>

          {/* Columna foto: postal redondeada con velo sol-lavado */}
          {hasMedia && (
            <div
              className="relative w-full overflow-hidden min-h-[300px]"
              style={{
                aspectRatio: '4 / 4.6',
                borderRadius: 'calc(var(--ms-radius) * 1.6)',
                border: '1px solid var(--ms-border)',
                boxShadow: 'var(--ms-card-shadow)',
              }}
            >
              {heroVideoUrl ? (
                <video autoPlay muted loop playsInline className="absolute inset-0 w-full h-full object-cover" src={heroVideoUrl} />
              ) : heroImageUrl ? (
                <Image src={heroImageUrl} alt={businessName} fill priority sizes="(max-width: 768px) 100vw, 45vw" className="object-cover" />
              ) : null}
              {/* Velo del tema (claro en la base, petróleo suave arriba) */}
              <div className="absolute inset-0" style={{ background: 'var(--ms-hero-overlay)', opacity: 0.55 }} />
            </div>
          )}
        </div>
      </div>

      {/* Divisor de ola cerrando la sección */}
      <svg
        className="block w-full"
        viewBox="0 0 1440 44"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden
        style={{ height: 34 }}
      >
        <path
          d="M0 24C120 8 240 0 360 6s240 26 360 30 240-6 360-16 240-12 360-2v26H0Z"
          fill="var(--ms-surface-2)"
        />
      </svg>
    </section>
  )
}
